import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useLiveQuery } from 'dexie-react-hooks';
import {
  audioNotes,
  BADGE,
  clockTime,
  correctionsFor,
  formatAccuracy,
  isTrainingGrade,
  livePhotos,
  projectEvidence,
  readingLabel,
  SOURCE_CAVEAT,
  SOURCE_LABEL,
  supersededPhotos,
  syncLatencyMs,
} from '@kilroy/shared';
import { db } from '../db/db';
import { useUsers } from '../hooks/useUsers';
import { useMediaUrl } from '../hooks/useMediaUrl';
import { formatDuration } from '../media/recorder';
import { describeEvidence } from '../components/EvidenceRow';
import './assignment.css';

/* The read view, plus the ledger.
 *
 * Nothing on this screen implies edit-in-place. The original values stay
 * visible with a line through them next to what replaced them, because the
 * point of the architecture is that both are still there. This is the screen
 * that has to win over the people who don't want a new system — Thomas and
 * Allan, by Brandon's own guess — so it shows its work.
 */

function Photo({ file }: { file: string }) {
  const url = useMediaUrl(file);
  return <div className="ed__photo">{url && <img src={url} alt="" />}</div>;
}

function Audio({ file, duration }: { file: string; duration?: number }) {
  const url = useMediaUrl(file);
  return (
    <div className="voicenote">
      <span className="grow mono">Voice note · {formatDuration(duration ?? 0)}</span>
      {url && <audio controls src={url} style={{ maxWidth: 190, height: 34 }} />}
    </div>
  );
}


export function EvidenceDetail() {
  const { userById } = useUsers();
  const [hero, setHero] = useState(0);
  const { id } = useParams<{ id: string }>();

  const original = useLiveQuery(() => (id ? db.evidence.get(id) : undefined), [id]);
  const corrections = useLiveQuery(
    () => (id ? db.corrections.where('evidence_id').equals(id).toArray() : []),
    [id],
    [],
  );
  const statuses = useLiveQuery(
    () => (id ? db.statuses.where('entity_id').equals(id).toArray() : []),
    [id],
    [],
  );

  if (!original) return <div className="screen" />;

  // The projected read view. The original is untouched underneath it.
  const e = projectEvidence(original, corrections);
  const photos = livePhotos(e);
  const gone = supersededPhotos(e);
  const audio = audioNotes(e);
  const log = correctionsFor(e.id, corrections);
  const latency = syncLatencyMs(e);

  const shown = photos[Math.min(hero, photos.length - 1)];

  return (
    <div className="screen screen--bleed-top">
      {/* The rock, edge to edge.
       *
       * On a phone the photograph can have the full width, and it should: this
       * is a geology app, the picture is the evidence, and a 90px thumbnail
       * above a table of values gets the priority exactly backwards. */}
      {shown && (
        <div className="ed__specimen">
          <div className="ed__frame">
            <Photo file={shown.file} />
            {isTrainingGrade(e) && <span className="ed__grade mono">training grade</span>}
          </div>

          {(photos.length > 1 || gone.length > 0) && (
            <div className="ed__strip">
              {photos.map((m, i) => (
                <button
                  key={m.id}
                  type="button"
                  className={`ed__thumb${i === hero ? ' is-on' : ''}`}
                  onClick={() => setHero(i)}
                  aria-label={`Photograph ${i + 1}`}
                >
                  <Photo file={m.file} />
                </button>
              ))}
              {/* A retake supersedes; it never deletes. Showing the old frame
                  greyed is the visible half of that promise. */}
              {gone.map((m) => (
                <span key={m.id} className="ed__thumb ed__thumb--gone" title="Superseded — kept">
                  <Photo file={m.file} />
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      <div className="screen__h">
        <h1 className="screen__t">{describeEvidence(e)}</h1>
        <p className="screen__s">
          {userById(e.user_id).name} · {clockTime(e.captured_at)}
          {e.entry_mode === 'backfill' && ` · ${BADGE.backfilled}`}
        </p>
      </div>

      {/* Somebody's words, at the time. The only prose on the screen, so it is
          set as prose rather than as another value in a list. */}
      {e.notes_text && (
        <blockquote className="ed__notes" data-selectable>
          {e.notes_text}
        </blockquote>
      )}

      {audio.map((m) => (
        <Audio key={m.id} file={m.file} duration={m.duration} />
      ))}

      <section className="mod">
        <div className="mod__h">
          <h2 className="label">Record</h2>
        </div>
        <div className="card card--pad">
        <div className="ed__facts">
          <span className="ed__key">Position</span>
          <span className="ed__val">
            {e.geometry.type === 'Point'
              ? `${e.geometry.coordinates[1].toFixed(5)}, ${e.geometry.coordinates[0].toFixed(5)}`
              : e.geometry.type}
          </span>

          <span className="ed__key">Accuracy</span>
          <span className="ed__val">
            {e.gps_accuracy_m < 0 ? 'not recorded' : formatAccuracy(e.gps_accuracy_m)}
          </span>

          {e.structural_feature && (
            <>
              <span className="ed__key">Reading</span>
              <span className="ed__val">{readingLabel(e.structural_feature)}</span>

              <span className="ed__key">Source</span>
              <span className="ed__val">
                {SOURCE_LABEL[e.structural_feature.reading_source]}
                {SOURCE_CAVEAT[e.structural_feature.reading_source] && (
                  <div style={{ color: 'var(--rust)', marginTop: 3 }}>
                    {SOURCE_CAVEAT[e.structural_feature.reading_source]}
                  </div>
                )}
              </span>

              <span className="ed__key">Confidence</span>
              <span className="ed__val">{e.structural_feature.confidence}</span>
            </>
          )}

          {e.rock_observation && e.rock_observation.alteration_type.length > 0 && (
            <>
              <span className="ed__key">Tags</span>
              <span className="ed__val">{e.rock_observation.alteration_type.join(', ')}</span>
            </>
          )}

          {/* Both clocks, always. The gap between them is the latency KPI, and
              hiding it would hide the number the whole pitch rests on. */}
          <span className="ed__key">Captured</span>
          <span className="ed__val">{clockTime(e.captured_at)}</span>

          <span className="ed__key">Reached team</span>
          <span className="ed__val">
            {e.received_at
              ? `${clockTime(e.received_at)}${latency != null ? ` · +${Math.round(latency / 60000)}m` : ''}`
              : 'still on this phone'}
          </span>
        </div>

        {e.notes_text && <p style={{ lineHeight: 1.5 }} data-selectable>{e.notes_text}</p>}
        </div>
      </section>

      {gone.length > 0 && (
        <section className="mod">
          <div className="mod__h">
            <h2 className="label">Superseded photos</h2>
            <span className="mod__n">{gone.length}</span>
          </div>
          <div className="card card--pad">
            <div className="ed__superseded">
              {gone.map((m) => (
                <Photo key={m.id} file={m.file} />
              ))}
            </div>
            <p className="hint">Kept, not deleted. A retake never removes the original.</p>
          </div>
        </section>
      )}

      <section className="mod">
        <div className="mod__h">
          <h2 className="label">History</h2>
        </div>
        <div className="card card--pad">
        <ul className="ledger">
          <li>
            <span className="ledger__mark">
              <span className="ledger__dot" />
            </span>
            <div>
              <div className="ledger__verb">Captured in the field</div>
              <div className="ledger__detail">
                {userById(e.user_id).name} · {clockTime(e.captured_at)} · {e.device_id.slice(0, 8)}
              </div>
            </div>
          </li>

          {log.map((c) => {
            const bySystem = String(c.corrected_by).startsWith('system:');
            return (
              <li key={c.id}>
                <span className="ledger__mark">
                  <span className={`ledger__dot${bySystem ? ' ledger__dot--system' : ''}`} />
                </span>
                <div>
                  <div className="ledger__verb">
                    {bySystem ? 'Transcribed' : `Corrected ${c.corrected_field.split('.').pop()?.replace(/_/g, ' ')}`}
                  </div>
                  <div className="ledger__detail">
                    {bySystem ? BADGE.machineTranscript : userById(String(c.corrected_by)).name} ·{' '}
                    {clockTime(c.corrected_at)}
                  </div>
                  {!bySystem && (
                    <div className="ledger__change">
                      <span className="ledger__old">{String(c.old_value ?? '—')}</span>
                      <span aria-hidden="true">→</span>
                      <span className="ledger__new">{String(c.new_value ?? '—')}</span>
                    </div>
                  )}
                </div>
              </li>
            );
          })}

          {statuses.map((s) => (
            <li key={s.id}>
              <span className="ledger__mark">
                <span
                  className={`ledger__dot${s.new_status === 'confirmed' ? ' ledger__dot--confirmed' : ''}`}
                />
              </span>
              <div>
                <div className="ledger__verb">
                  {s.new_status.replace(/_/g, ' ')}
                </div>
                <div className="ledger__detail">
                  {userById(s.changed_by).name} · {clockTime(s.changed_at)}
                  {s.note && ` · ${s.note}`}
                </div>
              </div>
            </li>
          ))}
        </ul>

        <p className="hint">
          Nothing on this list replaced anything. Every line was added.
        </p>
        </div>
      </section>
    </div>
  );
}
